import { useCallback, useEffect, useRef, useState } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"

const FINALIZED = "FINALIZED"

export const useCurrentEncounter = ({ interactionId }) => {
  const [currentEncounterId, setCurrentEncounterId] = useState(null)
  const [currentEncounterSequenceNumber, setCurrentEncounterSequenceNumber] = useState(null)
  const [currentEncounterStatus, setCurrentEncounterStatus] = useState(null)
  const [loading, setLoading] = useState(true)

  const multiPatientEnabled = useConfigFlag("multiPatient.enabled")
  const startNextPatientEnabled = useConfigFlag("multiPatient.startNextPatientEnabled")
  const refreshIntervalSeconds = useConfigFlag("multiPatient.refreshIntervalSeconds", 10)

  const latestSequence = useRef(0)
  const applyEncounter = useCallback((encounter) => {
    if (!encounter?.id) return
    const incoming = encounter.sequenceNumber ?? 0
    if (incoming && incoming < latestSequence.current) return
    latestSequence.current = incoming
    setCurrentEncounterId(encounter.id)
    setCurrentEncounterSequenceNumber(encounter.sequenceNumber ?? null)
    setCurrentEncounterStatus(encounter.status ?? null)
  }, [])

  useEffect(() => {
    latestSequence.current = 0
    setCurrentEncounterId(null)
    setCurrentEncounterSequenceNumber(null)
    setCurrentEncounterStatus(null)
  }, [interactionId])

  const fetchCurrentEncounter = useCallback(async ({ silent = false } = {}) => {
    if (!interactionId) {
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.getCurrentEncounter({ interactionId })
      applyEncounter(result?.data)
    } catch (err) {
      logger.error(`Failed to fetch current encounter for interaction ${interactionId}`, err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [interactionId, applyEncounter])

  useEffect(() => {
    if (!interactionId) {
      setLoading(false)
      return undefined
    }

    if (!multiPatientEnabled) {
      fetchCurrentEncounter()
      return undefined
    }

    fetchCurrentEncounter()

    const onEncounter = (encounter) => {
      if (!encounter?.id) {
        return
      }
      applyEncounter(encounter)
      setLoading(false)
    }

    const onError = (err) => {
      logger.error(`Encounter subscription failed for interaction ${interactionId}`, err)
      fetchCurrentEncounter()
    }

    const unsubscribe = dataService.IntakeQuestions.subscribeCurrentEncounter(onEncounter, {
      interactionId,
      onError,
    })

    return () => {
      if (Array.isArray(unsubscribe)) {
        unsubscribe.forEach((fn) => fn?.())
        return
      }
      unsubscribe?.()
    }
  }, [interactionId, multiPatientEnabled, fetchCurrentEncounter, applyEncounter])

  const canStartNextPatient = Boolean(multiPatientEnabled && startNextPatientEnabled)

  const startNextPatient = useCallback(async () => {
    if (!canStartNextPatient || !interactionId) return null
    setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.advanceEncounter({ interactionId })
      applyEncounter(result?.data)
      return result?.data || null
    } catch (err) {
      logger.error(`Failed to advance encounter for interaction ${interactionId}`, err)
      return null
    } finally {
      setLoading(false)
    }
  }, [canStartNextPatient, interactionId, applyEncounter])

  const isPastFirstEncounter = Boolean(currentEncounterSequenceNumber > 1)

  useEffect(() => {
    const intervalMs = Number(refreshIntervalSeconds) * 1000
    if (!interactionId || !multiPatientEnabled || !Number.isFinite(intervalMs) || intervalMs <= 0) {
      return undefined
    }
    const timer = setInterval(() => fetchCurrentEncounter({ silent: true }), intervalMs)
    return () => clearInterval(timer)
  }, [interactionId, multiPatientEnabled, refreshIntervalSeconds, fetchCurrentEncounter])

  const isEncounterFinalized = currentEncounterStatus === FINALIZED

  const refreshedForFinalized = useRef(null)
  useEffect(() => {
    if (!isEncounterFinalized || !currentEncounterId) return
    if (refreshedForFinalized.current === currentEncounterId) return
    refreshedForFinalized.current = currentEncounterId
    logger.debug(`Encounter ${currentEncounterId} is finalized -- resyncing`)
    fetchCurrentEncounter()
  }, [isEncounterFinalized, currentEncounterId, fetchCurrentEncounter])

  return {
    currentEncounterId,
    currentEncounterSequenceNumber,
    currentEncounterStatus,
    isEncounterFinalized,
    isPastFirstEncounter,
    canStartNextPatient,
    startNextPatient,
    loading,
    refetchEncounter: fetchCurrentEncounter,
  }
}
